import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { Box, Typography } from '@material-ui/core'

import { Dai } from 'components'
import { DisputeModal, DisputeResult } from './components'
import ActivityDisputeDaiButton from '../ActivityDisputeDaiButton'

import useSnackbar from 'hooks/useSnackbar'

import constants from 'constants/index'
import helpers from 'helpers'
import * as service from 'service'
import useStyles from './styles'

const {
  ACTIVITY_DISPUTE_MESSAGES,
  FAST_POLL_INTERVAL_MS,
  SLOW_POLL_INTERVAL_MS,
  FAST_POLL_COUNT,
  LOADING_MESSAGES,
  FETCHING_MESSAGES,
  POLL_STATUS,
  SNACKBAR_VARIANTS,
} = constants.dai.disputes

const ActivityDisputeDai = ({
  activity,
  governanceProjectId,
  canManageAnalysis,
}) => {
  const [openModal, setOpenModal] = useState(false)
  const [expanded, setExpanded] = useState(true)
  const [runId, setRunId] = useState(null)
  const [polling, setPolling] = useState(false)
  const [disputeResult, setDisputeResult] = useState(null)
  const [disputeError, setDisputeError] = useState(null)
  const [loadingMessage, setLoadingMessage] = useState(LOADING_MESSAGES[0])
  const [fetchFailed, setFetchFailed] = useState(false)

  const pollingRef = useRef(null)
  const pollCountRef = useRef(0)
  const isFetchingExistingRef = useRef(false)
  const classes = useStyles()
  const snackbar = useSnackbar()

  const question = activity?.governanceProjectQuestion

  useEffect(() => {
    const storedRunId = activity?.daiDisputeResponse?.runId
    if (storedRunId && !runId) {
      isFetchingExistingRef.current = true
      setRunId(storedRunId)
      setPolling(true)
      setFetchFailed(false)
      setLoadingMessage(FETCHING_MESSAGES[0])
    }
  }, [activity])

  const handleDisputeSuccess = (newRunId) => {
    isFetchingExistingRef.current = false
    setRunId(newRunId)
    setPolling(true)
    setDisputeResult(null)
    setDisputeError(null)
    setFetchFailed(false)
    setLoadingMessage(LOADING_MESSAGES[0])
  }

  const stopPolling = () => {
    if (pollingRef.current) {
      clearTimeout(pollingRef.current)
      pollingRef.current = null
    }
    setPolling(false)
  }

  useEffect(() => {
    if (!runId || !polling) return

    pollCountRef.current = 0

    const executePoll = async () => {
      try {
        const statusResponse = await service.dai.disputes.getStatus(runId)
        const status = statusResponse?.data?.dispute?.status

        if (status === POLL_STATUS.COMPLETED) {
          stopPolling()
          setDisputeResult(statusResponse?.data?.dispute?.result)
        } else if (
          status === POLL_STATUS.FAILED ||
          status === POLL_STATUS.ERROR
        ) {
          stopPolling()
          // Carregando uma contestação já existente: se falhar, esconde o componente.
          if (isFetchingExistingRef.current) {
            setFetchFailed(true)
            return
          }
          const errorMsg =
            statusResponse?.data?.dispute?.error ||
            ACTIVITY_DISPUTE_MESSAGES.GENERATION_ERROR
          setDisputeError(errorMsg)
          snackbar.open({
            message: errorMsg,
            variant: SNACKBAR_VARIANTS.ERROR,
          })
        } else {
          pollCountRef.current += 1
          const nextMessage = isFetchingExistingRef.current
            ? FETCHING_MESSAGES[
                Math.min(pollCountRef.current, FETCHING_MESSAGES.length - 1)
              ]
            : helpers.dai.getLoadingMessage(pollCountRef.current)
          setLoadingMessage(nextMessage)
          const interval =
            pollCountRef.current <= FAST_POLL_COUNT
              ? FAST_POLL_INTERVAL_MS
              : SLOW_POLL_INTERVAL_MS
          pollingRef.current = setTimeout(executePoll, interval)
        }
      } catch (error) {
        stopPolling()
        // Carregando uma contestação já existente: se falhar, esconde o componente.
        if (isFetchingExistingRef.current) {
          setFetchFailed(true)
          return
        }
        setDisputeError(ACTIVITY_DISPUTE_MESSAGES.STATUS_ERROR)
        snackbar.open({
          message: ACTIVITY_DISPUTE_MESSAGES.STATUS_ERROR,
          variant: SNACKBAR_VARIANTS.ERROR,
        })
      }
    }

    pollingRef.current = setTimeout(executePoll, FAST_POLL_INTERVAL_MS)

    return () => {
      if (pollingRef.current) clearTimeout(pollingRef.current)
    }
  }, [runId, polling])

  const hasContent =
    !fetchFailed && (polling || !!disputeResult || !!disputeError)

  return (
    <>
      {canManageAnalysis && (
        <Box className={classes.triggerContainer}>
          <ActivityDisputeDaiButton
            onClick={() => setOpenModal(true)}
            disabled={polling}
          />
        </Box>
      )}

      {hasContent && (
        <Dai.Card
          title="Contestação gerada pela DAI"
          expandable
          expanded={expanded}
          onToggleExpand={() => setExpanded((previous) => !previous)}
        >
          {polling && <Dai.Loading text={loadingMessage} loading />}

          {!!disputeError && !polling && (
            <Typography variant="body2" className={classes.errorText}>
              {disputeError}
            </Typography>
          )}

          {!!disputeResult && !polling && (
            <DisputeResult
              result={disputeResult}
              alternatives={question?.questionAlternatives}
            />
          )}
        </Dai.Card>
      )}

      {canManageAnalysis && (
        <DisputeModal
          open={openModal}
          setOpen={setOpenModal}
          activity={activity}
          governanceProjectId={governanceProjectId}
          onSuccess={handleDisputeSuccess}
        />
      )}
    </>
  )
}

ActivityDisputeDai.propTypes = {
  activity: PropTypes.object,
  // Vem de match.params (rota) → string; aceita number também (igual ao ActivityManageForm).
  governanceProjectId: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  canManageAnalysis: PropTypes.bool,
}

ActivityDisputeDai.defaultProps = {
  canManageAnalysis: false,
}

export default ActivityDisputeDai
